// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isNode(value: any): value is Node {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return value instanceof (getWindow(value) as any).Node;
}

function getDocumentElement(node: Node | Window): HTMLElement {
  return (
    (isNode(node) ? node.ownerDocument : node.document) || window.document
  ).documentElement;
}

function getWindow(node: Node | Window): Window {
  if (node == null) {
    return window;
  }

  if (!isWindow(node)) {
    const ownerDocument = node.ownerDocument;

    return ownerDocument ? ownerDocument.defaultView || window : window;
  }

  return node;
}

function getParentNode(node: Node): Node {
  if (getNodeName(node) === "html") {
    return node;
  }

  const result = node.parentNode || getDocumentElement(node);

  return result;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isWindow(value: any): value is Window {
  return (
    value &&
    value.document &&
    value.location &&
    value.alert &&
    value.setInterval
  );
}

function getNodeName(node: Node | Window): string {
  return isWindow(node) ? "" : node ? (node.nodeName || "").toLowerCase() : "";
}

function isLastTraversableNode(node: Node) {
  return ["html", "body", "#document"].includes(getNodeName(node));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isHTMLElement(value: any): value is HTMLElement {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return value instanceof (getWindow(value) as any).HTMLElement;
}

function isOverflowElement(element: Element): boolean {
  const { overflow, overflowX, overflowY, display } = getComputedStyle(element);

  return (
    /auto|scroll|overlay|hidden/.test(overflow + overflowY + overflowX) &&
    !["inline", "contents"].includes(display)
  );
}

function getNearestOverflowAncestor(node: Node): HTMLElement {
  const parentNode = getParentNode(node);

  if (isLastTraversableNode(parentNode)) {
    return node.ownerDocument!.body;
  }

  if (isHTMLElement(parentNode) && isOverflowElement(parentNode)) {
    return parentNode;
  }

  return getNearestOverflowAncestor(parentNode);
}

export function getOverflowAncestors(
  node: Node,
  list: Array<Element | Window> = []
): Array<Element | Window | VisualViewport> {
  const scrollableAncestor = getNearestOverflowAncestor(node);
  const isBody = scrollableAncestor === node.ownerDocument?.body;
  const win = getWindow(scrollableAncestor);
  const target = isBody
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ([win] as any).concat(
        win.visualViewport || [],
        isOverflowElement(scrollableAncestor) ? scrollableAncestor : []
      )
    : scrollableAncestor;

  const updatedList: (Window | Element | VisualViewport)[] =
    list.concat(target);

  return isBody
    ? updatedList
    : updatedList.concat(getOverflowAncestors(target));
}
